import { useNavigate } from 'react-router-dom';
import { Button, Icon } from '@/kit';
import { currentMove } from '@/domains/breakouts/engine';
import { routes } from '@/config/routes';
import { useMeStore } from '@/store/meStore';
import { MoveCard } from '@/components/MoveCard';
import { MomentumPanel } from '@/components/MomentumPanel';

export function TodayPage() {
  const navigate = useNavigate();
  const profile = useMeStore((s) => s.profile);
  const enrollment = useMeStore((s) => s.activeEnrollment());
  const momentum = useMeStore((s) => s.momentum);
  const recordMove = useMeStore((s) => s.recordMove);
  const moveRecords = useMeStore((s) => s.moveRecords);

  const current = enrollment ? currentMove(enrollment) : null;
  const alreadyDoneToday =
    enrollment &&
    moveRecords.some(
      (r) =>
        r.enrollmentId === enrollment.id &&
        r.day === enrollment.currentDay &&
        r.outcome === 'complete',
    );

  return (
    <div className="space-y-8">
      <header>
        <p className="text-[10px] font-extrabold uppercase tracking-hottr-stamp text-hottr-pink">
          Today
        </p>
        <h1 className="mt-1 font-display text-3xl font-black uppercase tracking-hottr-display">
          Your next Move
          <span className="text-hottr-pink">.</span>
        </h1>
        {profile?.stuckSummary && (
          <p className="mt-2 max-w-lg text-sm text-white/55">
            Working on: {profile.stuckSummary}
          </p>
        )}
      </header>

      {!current && (
        <div className="rounded-sm border border-white/12 bg-hottr-charcoal p-6">
          <p className="text-sm text-white/72">No active Breakout yet.</p>
          <Button
            className="mt-4"
            variant="primary"
            onClick={() => navigate(routes.breakout)}
          >
            Choose a Breakout
          </Button>
        </div>
      )}

      {current && alreadyDoneToday && (
        <div className="rounded-sm border border-hottr-pink/30 bg-hottr-pink/5 p-6">
          <p className="text-[10px] font-extrabold uppercase tracking-hottr-stamp text-hottr-pink">
            Day {enrollment?.currentDay} locked in
          </p>
          <h2 className="mt-2 font-display text-2xl font-black uppercase tracking-hottr-display">
            Move complete
          </h2>
          <p className="mt-2 text-sm text-white/72">
            Come back tomorrow for the next Move — or hit I&apos;m Close if the urge shows up.
          </p>
          <div className="mt-4 flex flex-col gap-2 sm:flex-row">
            <Button variant="primary" onClick={() => navigate(routes.imClose)}>
              I&apos;m Close
            </Button>
            <Button variant="secondary" onClick={() => navigate(routes.momentum)}>
              View Momentum
            </Button>
          </div>
        </div>
      )}

      {current && !alreadyDoneToday && (
        <MoveCard
          move={current.move}
          stage={current.stage}
          day={enrollment!.currentDay}
          durationDays={current.breakout.durationDays}
          onComplete={() => recordMove({ outcome: 'complete' })}
          onSkip={() => recordMove({ outcome: 'skip' })}
          onHard={() => recordMove({ outcome: 'hard' })}
        />
      )}

      <MomentumPanel metrics={momentum} />

      <button
        type="button"
        onClick={() => navigate(routes.imClose)}
        className="flex w-full items-center justify-between rounded-sm border border-hottr-pink/40 bg-hottr-pink/10 px-4 py-4 text-left transition-colors hover:bg-hottr-pink/15"
      >
        <span>
          <span className="block text-[10px] font-extrabold uppercase tracking-hottr-stamp text-hottr-pink">
            Decision point
          </span>
          <span className="mt-1 block text-[14px] font-semibold">
            I&apos;m Close — need one action now
          </span>
        </span>
        <Icon name="bolt" className="text-hottr-pink text-[28px]" />
      </button>
    </div>
  );
}
